/**
 * Papier Royal — Cart Companions (Horizon theme)
 *
 * Module de cross-sell pour le cart drawer du thème Horizon (Shopify nouvelle gen).
 *
 * Architecture :
 *   - Le mount-point [data-cart-companions-mount] est dans header-actions.liquid,
 *     entre cart-products et cart-summary. Il est inclus dans la section morphée,
 *     donc préservé à chaque cart update (mais son contenu est vidé par le morph).
 *   - On écoute l'event natif Horizon `cart:update` (émis par CartAddEvent après
 *     que morphSection a terminé). Cet event détient resource = la ligne ajoutée.
 *   - À chaque cart:update, on fetch la section cart-companions via Section
 *     Rendering API et on (ré)injecte dans le mount-point.
 *   - Si plus d'item dans le cart → mount vidé.
 *   - Si la dernière ligne ajoutée n'a pas de companions configurés → mount vidé.
 *
 * Pas de window.fetch override. Pas d'interception cart/add. Pur event-driven.
 */

(function () {
  'use strict';

  const config = Object.assign(
    {
      mountSelector: '[data-cart-companions-mount]',
      sectionId: 'cart-companions',
      // Auto-ouvre le drawer sur cart:update. Nécessaire parce que Customily
      // bypass le wrapper d'ATC natif du thème qui aurait ouvert le drawer.
      // Set à false dans window.PR_CART_COMPANIONS si tu veux désactiver.
      autoOpenDrawer: true,
      drawerSelector: 'cart-drawer-component',
      debug: false
    },
    window.PR_CART_COMPANIONS || {}
  );

  const log = (...args) => {
    if (config.debug) console.log('[PR companions]', ...args);
  };

  // Track de la ligne déclencheuse (handle) — survit aux morphs
  let lastInitiatorHandle = null;
  let inFlight = null;

  // ---------- Wire events ----------
  // Horizon émet `cart:update` après chaque add/update/morph terminé.
  // detail.resource = line item ajouté (avec product_handle), detail.sourceId, detail.sections.
  // On garde aussi quelques events legacy au cas où certaines apps tierces les émettent.

  ['cart:update', 'cart:added', 'product:added-to-cart'].forEach((evt) => {
    document.addEventListener(evt, handleCartEvent);
  });

  function handleCartEvent(e) {
    const detail = e?.detail || {};
    const resource = detail.resource || detail.item || detail.product || {};
    // Différentes formes possibles selon l'event source
    const handle =
      resource.product_handle ||
      resource.handle ||
      detail.handle ||
      (typeof resource === 'object' && resource.url && extractHandleFromUrl(resource.url));

    // Ouvre le drawer si pas déjà ouvert (Customily bypass l'auto-open natif)
    if (config.autoOpenDrawer) {
      openDrawerIfClosed();
    }

    if (handle) {
      lastInitiatorHandle = handle;
      log('handle from event', handle, 'via', e.type);
      loadCompanions(handle);
    } else {
      // Fallback : récupérer le handle depuis /cart.js si l'event n'a pas porté l'info
      log('no handle in event, fallback to /cart.js');
      resolveLastInitiatorFromCart().then((h) => {
        if (h) {
          lastInitiatorHandle = h;
          loadCompanions(h);
        }
      });
    }
  }

  function openDrawerIfClosed() {
    const drawer = document.querySelector(config.drawerSelector);
    if (!drawer) {
      log('drawer not found', config.drawerSelector);
      return;
    }
    // Guards : déjà ouvert ?
    if (drawer.hasAttribute('open')) return;
    const dialog = drawer.querySelector('dialog');
    if (dialog?.open) return;

    try {
      if (typeof drawer.open === 'function') {
        drawer.open();
        log('drawer.open() called');
      } else if (dialog?.showModal) {
        dialog.showModal();
        log('dialog.showModal() called');
      }
    } catch (err) {
      log('drawer open failed', err);
    }
  }

  function extractHandleFromUrl(url) {
    const m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async function resolveLastInitiatorFromCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      // Customily ajoute des "extra products" (upsells de personnalisation) au cart
      // après l'ATC du produit principal. Ils ont _customily-upsell-id dans
      // leurs properties (le main item a _customily-upsell-main-id à la place).
      // On filtre ces upsells pour trouver le vrai initiator.
      const initiators = cart.items.filter((item) => {
        const props = item.properties || {};
        return !props['_customily-upsell-id'];
      });
      // IMPORTANT : Shopify cart.items est en ordre reverse-chronologique
      // (most recent FIRST). Le dernier ajout = items[0], pas items[length-1].
      // Confirmé empiriquement 2026-05-17 sur ce shop avec un cart [card, notebook]
      // où l'ordre côté API était [notebook, card].
      const last = initiators[0];
      return last?.handle || null;
    } catch (e) {
      log('cart.js fetch failed', e);
      return null;
    }
  }

  // ---------- Fetch section via Section Rendering API ----------

  async function loadCompanions(handle) {
    if (!handle) {
      clearMount();
      return;
    }

    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;

    try {
      // NB: pas de header `Accept: application/json` ici. Avec ce header,
      // Shopify ignore le `?sections=` et renvoie le produit JSON brut
      // (45kB de produit complet) au lieu de `{ "cart-companions": "<html>" }`.
      // Sans header, Shopify retourne bien la section. Confirmé 2026-05-17
      // via diff entre fetch manuel (sans Accept) qui marche et notre fetch
      // (avec Accept) qui retournait du JSON produit où la section n'existait pas.
      // Préfixe de LANGUE (2026-07-31) : sans `Shopify.routes.root`, l'appel partait sur `/products/…`
      // et Shopify rendait la section dans la locale PAR DÉFAUT → la bande s'affichait en anglais
      // (« Complete your set », « Add ») sur les pages FR, alors que les traductions existent.
      // `routes.root` vaut '/' en EN et '/fr/' en FR.
      const localeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const res = await fetch(
        `${localeRoot}products/${encodeURIComponent(handle)}?sections=${config.sectionId}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const html = json[config.sectionId];
      if (!html) {
        log('empty section response');
        clearMount();
        return;
      }
      injectCompanions(html, handle);
    } catch (e) {
      if (e.name !== 'AbortError') log('fetch error', e);
    } finally {
      inFlight = null;
    }
  }

  // ---------- DOM injection ----------

  function getMount() {
    return document.querySelector(config.mountSelector);
  }

  function clearMount() {
    const mount = getMount();
    if (mount) {
      mount.innerHTML = '';
      mount.setAttribute('aria-hidden', 'true');
    }
    lastInjectedHtml = null;
    lastInjectedHandle = null;
  }

  // Cache de la dernière injection réussie, pour ré-injection après wipe externe
  // (morphSection sur cart:update vide le mount, parfois aussi sur events tardifs
  // dispatchés par Customily/web-pixel — d'où le besoin d'un guardian).
  let lastInjectedHtml = null;
  let lastInjectedHandle = null;
  let mountObserver = null;

  function ensureMountObserver() {
    const mount = getMount();
    if (!mount) return;
    // Re-attach si l'observer existe mais cible un mount déconnecté (morph aurait
    // pu remplacer la div mount entière au lieu de la vider in-place).
    if (mountObserver) {
      try { mountObserver.disconnect(); } catch {}
    }
    mountObserver = new MutationObserver(() => {
      const current = getMount();
      if (!current) return;
      // Wipe externe détecté : mount vidé mais on a un dernier état connu.
      if (current.children.length === 0 && lastInjectedHtml && lastInjectedHandle) {
        log('mount wiped externally — re-injecting from cache');
        // Bypass cache update path : on appelle directement le rendering avec le
        // dernier handle connu. Pas de fetch — synchrone.
        injectCompanions(lastInjectedHtml, lastInjectedHandle, { fromCache: true });
      }
    });
    mountObserver.observe(mount, { childList: true });
  }

  function injectCompanions(html, handle, opts) {
    const mount = getMount();
    if (!mount) {
      log('no mount point', config.mountSelector);
      return;
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const section = tmp.querySelector('[data-pr-companions]');

    if (!section) {
      // Aucun companion configuré pour ce produit
      clearMount();
      return;
    }

    // Update cache (sauf si on est déjà en train de ré-injecter depuis le cache,
    // auquel cas html === lastInjectedHtml de toute façon)
    if (!opts || !opts.fromCache) {
      lastInjectedHtml = html;
      lastInjectedHandle = handle;
    }

    mount.replaceChildren(section);
    mount.setAttribute('aria-hidden', 'false');

    // Reveal animation (la classe --visible déclenche le fade-in CSS)
    requestAnimationFrame(() => {
      section.classList.add('pr-companions--visible');
      // Smooth scroll vers le bloc UNIQUEMENT si pas déjà visible dans le
      // drawer viewport. Sur cart court il sera visible d'office (skip).
      // Sur cart long il est sous le fold → on amène l'utilisateur dessus
      // pour qu'il ne loupe pas la suggestion.
      if (!opts || !opts.fromCache) {
        scrollCompanionsIntoViewIfNeeded(section);
      }
    });

    ensureMountObserver();
    wireCompanions(section, handle);
  }

  function scrollCompanionsIntoViewIfNeeded(section) {
    const dialog = section.closest('dialog');
    if (!dialog || !dialog.open) return; // skip si drawer fermé
    const dialogRect = dialog.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const fullyVisible =
      sectionRect.top >= dialogRect.top &&
      sectionRect.bottom <= dialogRect.bottom;
    if (fullyVisible) return;
    try {
      section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch {
      // Browsers anciens : fallback brut
      section.scrollIntoView();
    }
  }

  // ---------- Companion card handlers ----------

  function wireCompanions(section, initiatorHandle) {
    const syncEnabled = section.dataset.prSync === '1';
    const cards = section.querySelectorAll('[data-pr-companion]');

    // Matching qty UNIQUEMENT pour le flow sync (card↔envelope). Pour les
    // recommandations génériques (notebook→pen etc.), 1 pen ≠ besoin d'égaler
    // la qty notebook ; on garde "ADD" simple, qty=1.
    if (syncEnabled) {
      resolveInitiatorQuantity(initiatorHandle).then((matchingQty) => {
        if (!matchingQty || matchingQty <= 1) return;
        cards.forEach((card) => {
          if (!card.hasAttribute('data-pr-companion-sync')) return;
          const h = card.dataset.prCompanionHandle;
          if (!h) return;
          try {
            sessionStorage.setItem(
              `pr_companion_qty__${h}`,
              JSON.stringify({ qty: matchingQty, initiator: initiatorHandle })
            );
          } catch {}
          const qtyEl = card.querySelector('[data-pr-companion-add-qty]');
          if (qtyEl) {
            qtyEl.textContent = ' ' + matchingQty;
            qtyEl.hidden = false;
          }
        });
      });
    }

    // Customily inheritance (si sync activé sur l'initiator) — on capture le
    // design pour le persister vers la PDP (notice "Continue your personalization")
    // et pour propager les properties sur l'ATC du companion. On N'écrit PAS
    // le preview Customily sur le thumbnail (volonté UX : laisser l'image
    // produit générique de l'enveloppe visible, pas l'overlay perso).
    let inheritedDesign = null;
    if (syncEnabled && window.PRCustomilyBridge) {
      window.PRCustomilyBridge.captureFromCart(initiatorHandle).then((design) => {
        inheritedDesign = design;
        log('inherited design', design);
        // Persist seulement, pas de renderInheritedPreviews
        if (design) {
          cards.forEach((card) => {
            if (!card.hasAttribute('data-pr-companion-sync')) return;
            const companionHandle = card.dataset.prCompanionHandle;
            if (companionHandle && window.PRCustomilyBridge?.persistForPDP) {
              window.PRCustomilyBridge.persistForPDP(companionHandle, design);
            }
          });

          // AUTO-SUITE : en plus du persist per-handle (companions cartés
          // explicitement dans le drawer), on stocke le design sous une clé
          // globale. Avantage : n'importe quelle PDP Customily-enabled visitée
          // ensuite peut proposer "Reprendre votre personnalisation" — pas
          // besoin d'avoir déclaré le produit comme companion via metafield.
          // Couvre business-card → letterhead → envelope automatiquement.
          if (window.PRCustomilyBridge?.persistForSuite) {
            window.PRCustomilyBridge.persistForSuite(design);
          }
        }
      });
    }

    cards.forEach((card) => {
      const addBtn = card.querySelector('[data-pr-companion-add]');
      const cardSync = card.hasAttribute('data-pr-companion-sync');

      addBtn?.addEventListener('click', async (e) => {
        e.preventDefault();
        if (card.classList.contains('is-loading')) return;
        card.classList.add('is-loading');

        try {
          const variantId = card.dataset.prCompanionId;
          const properties = {};

          if (cardSync && inheritedDesign && window.PRCustomilyBridge) {
            Object.assign(
              properties,
              window.PRCustomilyBridge.serializeForLineItem(inheritedDesign)
            );
          }

          // Quantité matched UNIQUEMENT pour companions sync (card↔envelope).
          // Pour les recos génériques, qty=1 (ajouter 1 pen pour 50 notebooks
          // n'a aucun sens).
          const matchingQty = cardSync
            ? await resolveInitiatorQuantity(initiatorHandle)
            : 1;

          await addToCart(variantId, properties, matchingQty);

          card.classList.remove('is-loading');
          card.classList.add('is-added');
          setTimeout(() => card.classList.remove('is-added'), 1800);

          // Horizon va re-morpher la section et émettre cart:update,
          // ce qui re-déclenchera loadCompanions automatiquement.
          // Pas besoin de dispatch manuel.
        } catch (err) {
          card.classList.remove('is-loading');
          card.classList.add('is-error');
          setTimeout(() => card.classList.remove('is-error'), 2400);
          log('add-to-cart failed', err);
        }
      });
    });
  }

  // Renvoie la quantité de l'initiator à matcher sur le companion.
  // On prend uniquement la PLUS RÉCENTE line item (matches[0]) — pas la somme
  // de toutes les BC du cart. Rationale : le drawer s'ouvre parce que le client
  // VIENT d'ajouter cet item ; le companion doit matcher CE add spécifique, pas
  // le total cumulé. Bug observé 2026-05-21 : cart avec 2 BC lines (50×200 +
  // 1×50) → "ADD 10050" alors qu'on voulait juste 50 envelopes pour le nouveau.
  // Cohérent avec extractDesign() qui aussi prend matches[0].
  //
  // Cas Papier Royal : variants encodent la taille du set ("100 Cards" etc.)
  // avec item.quantity = 1 → on multiplie setSize × item.quantity.
  // Filtré des upsells Customily (_customily-upsell-id).
  async function resolveInitiatorQuantity(initiatorHandle) {
    if (!initiatorHandle) return 1;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const matches = cart.items.filter((item) => {
        const props = item.properties || {};
        return item.handle === initiatorHandle && !props['_customily-upsell-id'];
      });
      const latest = matches[0];
      if (!latest) return 1;
      const setSize = extractSetSizeFromItem(latest) || 1;
      const qty = setSize * (Number(latest.quantity) || 1);
      return qty > 0 ? qty : 1;
    } catch (e) {
      log('resolveInitiatorQuantity failed', e);
      return 1;
    }
  }

  // Cherche un nombre dans variant_options puis variant_title (pattern
  // type "100 Cards", "50 cartes", "200 sheets"). Retourne null si rien.
  function extractSetSizeFromItem(item) {
    const SET_RX = /(\d+)\s*(card|carte|piece|sheet|feuille|item)s?/i;
    const opts = Array.isArray(item.variant_options) ? item.variant_options : [];
    for (const opt of opts) {
      const m = String(opt).match(SET_RX);
      if (m) return parseInt(m[1], 10);
    }
    if (item.variant_title) {
      const m = item.variant_title.match(SET_RX);
      if (m) return parseInt(m[1], 10);
    }
    return null;
  }

  async function addToCart(variantId, properties, quantity) {
    const qty = Math.max(1, Number(quantity) || 1);
    const body = {
      items: [
        {
          id: Number(variantId),
          quantity: qty,
          properties: Object.keys(properties).length ? properties : undefined
        }
      ],
      // Demande à Shopify de renvoyer les sections morphées dans la réponse,
      // pour que Horizon puisse re-morpher le drawer (sinon il faudrait
      // déclencher manuellement le refresh).
      sections: getMorphableSections(),
      sections_url: window.location.pathname
    };
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('add-to-cart failed');
    const data = await res.json();

    // Dispatch cart:update avec la SHAPE attendue par Atelier :
    // - resource = la cart entière (data de /cart/add.js réponse)
    // - data.sections = HTML des sections morphables (pour component-cart-items.js
    //   qui lit event.detail.data.sections, PAS event.detail.sections)
    // Sans la nested `data`, le morph ne fire pas → totals/qty pas mis à jour.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'pr-companion',
          data: {
            sections: data.sections,
            source: 'pr-companion',
            itemCount: data.item_count,
            variantId: String(variantId)
          }
        }
      })
    );

    return data;
  }

  /**
   * Liste les section IDs morphables par Horizon (cart drawer principalement).
   * Horizon expose typiquement des data-attributes sur les sections morphables.
   */
  function getMorphableSections() {
    const sections = new Set();
    document.querySelectorAll('[data-section-id], [id^="shopify-section-"]').forEach((el) => {
      const id = el.dataset.sectionId || el.id.replace('shopify-section-', '');
      // On limite aux sections qui contiennent le cart drawer pour éviter
      // de demander tout le DOM
      if (
        id &&
        (el.querySelector('[data-cart-companions-mount]') ||
          el.querySelector('cart-drawer-component') ||
          id.includes('header'))
      ) {
        sections.add(id);
      }
    });
    return Array.from(sections).join(',');
  }

  // ---------- Public API ----------

  window.PRCartCompanions = {
    refresh: (handle) => loadCompanions(handle || lastInitiatorHandle),
    clear: clearMount,
    _internals: { lastInitiatorHandle: () => lastInitiatorHandle }
  };

  // ---------- Init au load : companions persistants ----------
  // Sans ça, les companions n'apparaissent qu'après un cart:update (ATC). Au
  // re-load de page ou réouverture du drawer, le mount est vide. On hydrate
  // donc au DOMContentLoaded en résolvant le dernier item non-upsell du cart.
  async function init() {
    try {
      const handle = await resolveLastInitiatorFromCart();
      if (handle) {
        lastInitiatorHandle = handle;
        loadCompanions(handle);
      }
    } catch (e) {
      log('init failed', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  log('ready (Horizon mode)');
})();
// cache-bust: 2026-05-21-hint-json-format
